import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Auth } from '@angular/fire/auth';
import { Observable, from, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Attaches the signed-in user's Firebase ID token as `Authorization: Bearer <token>`
 * to every request hitting our own API (`environment.app_api_url`).
 *
 * This is the keystone for the backend `withAuth(...)` middleware: the Azure
 * Functions key (added by FunctionsKeyInterceptor) satisfies `authLevel: "function"`,
 * and this Bearer token satisfies the per-user/company Firebase auth check.
 *
 * Safety rules:
 * - Only targets our API host, so tokens are never leaked to third-party URLs.
 * - Never overrides an `Authorization` header a service set explicitly (e.g.
 *   payment/keys services that already attach their own token).
 * - If there is no signed-in user, the request passes through unchanged and the
 *   backend returns 401 — anonymous/public endpoints simply ignore the header.
 */
@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  private auth = inject(Auth);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const apiBaseUrl = environment.app_api_url;

    if (
      !apiBaseUrl ||
      !this.targetsApi(req.url, apiBaseUrl) ||
      req.headers.has('Authorization')
    ) {
      return next.handle(req);
    }

    const user = this.auth.currentUser;
    if (!user) {
      return next.handle(req);
    }

    return from(user.getIdToken()).pipe(
      switchMap((token) =>
        next.handle(
          token
            ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
            : req,
        ),
      ),
    );
  }

  private targetsApi(requestUrl: string, apiBaseUrl: string): boolean {
    try {
      const baseOrigin =
        typeof globalThis.location === 'undefined'
          ? undefined
          : globalThis.location.origin;
      const apiUrl = new URL(apiBaseUrl, baseOrigin);
      const targetUrl = new URL(requestUrl, baseOrigin);
      const apiPath = apiUrl.pathname.replace(/\/$/, '');

      return (
        targetUrl.origin === apiUrl.origin &&
        (apiPath === '' ||
          targetUrl.pathname === apiPath ||
          targetUrl.pathname.startsWith(`${apiPath}/`))
      );
    } catch {
      return false;
    }
  }
}
